/**
 * Admin-only Staff account management. Maps to
 * apps/accounts/admin_views.py::AdminStaffViewSet.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  Paginated,
  StaffCreateInput,
  StaffCreateResponse,
  StaffResetPasswordResponse,
  User,
} from "@/types/api";

const STAFF_KEY = ["staff"];

export function useStaffList() {
  return useQuery<Paginated<User>>({
    queryKey: STAFF_KEY,
    queryFn: async () => {
      const r = await api.get<Paginated<User>>("/v1/auth/staff/");
      return r.data;
    },
  });
}

export function useCreateStaff() {
  const queryClient = useQueryClient();
  return useMutation<StaffCreateResponse, unknown, StaffCreateInput>({
    mutationFn: async (input) => {
      const r = await api.post<StaffCreateResponse>("/v1/auth/staff/", input);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STAFF_KEY }),
  });
}

export function useSetStaffActive() {
  const queryClient = useQueryClient();
  return useMutation<User, unknown, { id: string; active: boolean }>({
    mutationFn: async ({ id, active }) => {
      const action = active ? "reactivate" : "deactivate";
      const r = await api.patch<User>(`/v1/auth/staff/${id}/${action}/`);
      return r.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STAFF_KEY }),
  });
}

export function useResetStaffPassword() {
  return useMutation<StaffResetPasswordResponse, unknown, { id: string; password: string }>({
    mutationFn: async ({ id, password }) => {
      const r = await api.post<StaffResetPasswordResponse>(
        `/v1/auth/staff/${id}/reset-password/`,
        { password }
      );
      return r.data;
    },
  });
}
